import { CognitoUserPool, ICognitoUserPoolData } from 'amazon-cognito-identity-js';
import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import { DynamoDBDocumentClient } from '@aws-sdk/lib-dynamodb';
import { S3Client } from '@aws-sdk/client-s3';
import { RekognitionClient } from '@aws-sdk/client-rekognition';
import { CognitoIdentityProviderClient } from '@aws-sdk/client-cognito-identity-provider';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import type { CognitoUserSession } from 'amazon-cognito-identity-js';

// AWS Configuration
// Replace these with your actual AWS values
export const awsConfig = {
  region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
  
  // Cognito Configuration
  cognito: {
    userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || 'us-east-1_XXXXXXXXX',
    clientId: process.env.REACT_APP_COGNITO_CLIENT_ID || 'your-client-id-here',
    domain: process.env.REACT_APP_COGNITO_DOMAIN || 'glowlens-users',
    identityPoolId: process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID || '',
    // OAuth redirect URLs (REACT_APP_REDIRECT_URI is a common alias for sign-in callback)
    redirectSignIn:
      process.env.REACT_APP_COGNITO_REDIRECT_SIGNIN ||
      process.env.REACT_APP_REDIRECT_URI ||
      'https://glowlens.net/callback',
    redirectSignOut: process.env.REACT_APP_COGNITO_REDIRECT_SIGNOUT || 'https://glowlens.net',
    // Social provider client IDs
    googleClientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
    appleClientId: process.env.REACT_APP_APPLE_CLIENT_ID || '',
  },
  
  // DynamoDB Table Names
  dynamodb: {
    usersTable: process.env.REACT_APP_DYNAMODB_USERS_TABLE || 'glowlens-users',
    scansTable: process.env.REACT_APP_DYNAMODB_SCANS_TABLE || 'glowlens-scans',
    lifestyleTable: process.env.REACT_APP_DYNAMODB_LIFESTYLE_TABLE || 'glowlens-lifestyle',
    referralsTable: process.env.REACT_APP_DYNAMODB_REFERRALS_TABLE || 'glowlens-referrals',
    progressTable: process.env.REACT_APP_DYNAMODB_PROGRESS_TABLE || 'glowlens-progress',
    scansByScanIdIndex: process.env.REACT_APP_DYNAMODB_SCANS_BY_SCAN_ID_INDEX,
  },
  
  // S3 Configuration
  s3: {
    bucket: process.env.REACT_APP_S3_BUCKET || 'glowlens-scan-images',
    region: process.env.REACT_APP_S3_REGION || 'us-east-1',
    publicBaseUrl: process.env.REACT_APP_S3_PUBLIC_BASE_URL || '',
  },
};

// Initialize Cognito User Pool
const poolData: ICognitoUserPoolData = {
  UserPoolId: awsConfig.cognito.userPoolId,
  ClientId: awsConfig.cognito.clientId,
};

export const userPool = new CognitoUserPool(poolData);

// Initialize DynamoDB Document Client
const dynamoClient = new DynamoDBClient({
  region: awsConfig.region,
  // Credentials will be provided by Cognito Identity Pool or environment
});

export const docClient = DynamoDBDocumentClient.from(dynamoClient, {
  marshallOptions: {
    removeUndefinedValues: true,
    convertClassInstanceToMap: true,
  },
});

// Initialize S3 Client
export const s3Client = new S3Client({
  region: awsConfig.s3.region,
  // Credentials will be provided by Cognito Identity Pool
});

// Initialize Rekognition Client
export const rekognitionClient = new RekognitionClient({
  region: awsConfig.region,
  // Credentials will be provided by Cognito Identity Pool
});

const buildCredentialProvider = (session: CognitoUserSession) => {
  if (!awsConfig.cognito.identityPoolId) {
    throw new Error('Missing Cognito Identity Pool ID. Set REACT_APP_COGNITO_IDENTITY_POOL_ID.');
  }

  const idToken = session.getIdToken().getJwtToken();

  return fromCognitoIdentityPool({
    identityPoolId: awsConfig.cognito.identityPoolId,
    logins: {
      [`cognito-idp.${awsConfig.region}.amazonaws.com/${awsConfig.cognito.userPoolId}`]: idToken,
    },
    clientConfig: { region: awsConfig.region },
  });
};

export const createAuthedS3Client = (session: CognitoUserSession): S3Client => {
  return new S3Client({
    region: awsConfig.s3.region,
    credentials: buildCredentialProvider(session),
  });
};

export const createAuthedDocumentClient = (session: CognitoUserSession): DynamoDBDocumentClient => {
  const client = new DynamoDBClient({
    region: awsConfig.region,
    credentials: buildCredentialProvider(session),
  });

  return DynamoDBDocumentClient.from(client, {
    marshallOptions: {
      removeUndefinedValues: true,
      convertClassInstanceToMap: true,
    },
  });
};

export const createAuthedRekognitionClient = (session: CognitoUserSession): RekognitionClient => {
  return new RekognitionClient({
    region: awsConfig.region,
    credentials: buildCredentialProvider(session),
  });
};

// Initialize Cognito Identity Provider Client
export const cognitoClient = new CognitoIdentityProviderClient({
  region: awsConfig.region,
});

// Export configuration
export default awsConfig;


